import { useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  formatScheduleDayHeading,
  formatWallMinutesOnDate,
  resolveScheduleTimeZone,
} from '@/lib/schedule-timezone';
import { cn } from '@/lib/utils';
import type { FormField } from '@/types/form-definition';

type DayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
const DAY_KEYS: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toDateOnlyString(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

function parseDateOnly(value: unknown): Date | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  try {
    const d = parseISO(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  } catch {
    return undefined;
  }
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function getDayKeyFromDate(d: Date): DayKey {
  return DAY_KEYS[d.getDay()] ?? 'mon';
}

function parseHHmmToMinutes(value: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hh = Number.parseInt(m[1], 10);
  const mm = Number.parseInt(m[2], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  if (hh < 0 || hh > 23) return null;
  if (mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

function clampDate(d: Date, min: Date, max: Date) {
  if (d < min) return min;
  if (d > max) return max;
  return d;
}

type Slot = { minutes: number; label: string };

function computeHorizonEnd(config: Record<string, unknown>, today: Date) {
  const bw = isPlainObject(config.bookingWindow) ? (config.bookingWindow as Record<string, unknown>) : {};
  const mode = typeof bw.mode === 'string' ? String(bw.mode) : 'rolling';
  if (mode === 'absolute') {
    const end = parseDateOnly(bw.endDate);
    return end ?? addDays(today, 365);
  }
  const rolling =
    typeof bw.rollingDaysAhead === 'number' && Number.isFinite(bw.rollingDaysAhead)
      ? Math.max(1, Math.min(730, Number(bw.rollingDaysAhead)))
      : 365;
  return addDays(today, rolling);
}

function computeSlotsForDate(config: Record<string, unknown>, date: Date, timeZone: string): Slot[] {
  const iso = toDateOnlyString(date);
  const blackout = new Set((Array.isArray(config.blackoutDates) ? (config.blackoutDates as any[]) : []).map(String));
  if (blackout.has(iso)) return [];

  const duration =
    typeof config.slotDurationMinutes === 'number' ? Math.max(5, Math.min(1440, Number(config.slotDurationMinutes))) : 30;
  const interval =
    typeof config.slotIntervalMinutes === 'number' ? Math.max(5, Math.min(1440, Number(config.slotIntervalMinutes))) : 30;

  const overrides = isPlainObject(config.dateOverrides) ? (config.dateOverrides as Record<string, unknown>) : {};
  const override = isPlainObject(overrides[iso]) ? (overrides[iso] as Record<string, unknown>) : null;
  if (override?.closed === true) return [];

  const weekly = isPlainObject(config.weeklySchedule) ? (config.weeklySchedule as Record<string, unknown>) : {};
  const dayKey = getDayKeyFromDate(date);
  const daySchedule = isPlainObject(weekly[dayKey]) ? (weekly[dayKey] as Record<string, unknown>) : {};
  if (override?.ranges == null && daySchedule.enabled !== true) return [];

  const rangesRaw =
    override && Array.isArray(override.ranges)
      ? (override.ranges as unknown[])
      : Array.isArray(daySchedule.ranges)
        ? (daySchedule.ranges as unknown[])
        : [];

  const ranges = rangesRaw
    .map((r) => (isPlainObject(r) ? (r as Record<string, unknown>) : null))
    .filter((r): r is Record<string, unknown> => Boolean(r))
    .map((r) => ({
      startMin: typeof r.start === 'string' ? parseHHmmToMinutes(r.start) : null,
      endMin: typeof r.end === 'string' ? parseHHmmToMinutes(r.end) : null,
    }))
    .filter((r): r is { startMin: number; endMin: number } => r.startMin !== null && r.endMin !== null)
    .filter((r) => r.startMin < r.endMin)
    .sort((a, b) => a.startMin - b.startMin);

  const out: Slot[] = [];
  for (const r of ranges) {
    for (let t = r.startMin; t + duration <= r.endMin; t += interval) {
      out.push({ minutes: t, label: formatWallMinutesOnDate(iso, t, timeZone) });
    }
  }
  return out;
}

export function AppointmentPickerPreview({
  field,
  className,
}: {
  field: FormField;
  className?: string;
}) {
  const config = (field.config ?? {}) as Record<string, unknown>;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!rootRef.current) return;
    const el = rootRef.current;

    const ro = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect?.width ?? 0;
      setContainerWidth((prev) => (Math.abs(prev - next) >= 1 ? next : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const horizonEnd = useMemo(() => computeHorizonEnd(config, today), [config, today]);
  const [selectedDate, setSelectedDate] = useState<Date>(() => today);
  const [selectedMinutes, setSelectedMinutes] = useState<number | null>(null);

  const scheduleTz = useMemo(
    () => resolveScheduleTimeZone(typeof config.scheduleTimeZone === 'string' ? config.scheduleTimeZone : undefined),
    [config.scheduleTimeZone]
  );

  const boundedSelectedDate = useMemo(
    () => clampDate(selectedDate, today, horizonEnd),
    [selectedDate, today, horizonEnd]
  );

  const selectedYmd = useMemo(() => toDateOnlyString(boundedSelectedDate), [boundedSelectedDate]);

  const slots = useMemo(
    () => computeSlotsForDate(config, boundedSelectedDate, scheduleTz),
    [config, boundedSelectedDate, scheduleTz]
  );

  const tzLabel = `Times in ${scheduleTz}`;

  // Use container width (not viewport) so this stays responsive inside resizable previews.
  const isTwoColumn = containerWidth >= 900;
  const isSlotsScrollable = containerWidth >= 1024;

  return (
    <div ref={rootRef} className={cn('rounded-xl border bg-background shadow-sm', className)}>
      <div className={cn('grid gap-6 p-5', isTwoColumn && 'grid-cols-[320px_1fr]')}>
        <section className="rounded-xl border bg-card p-4">
          <header className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-muted-foreground">Date</p>
              <p className="mt-1 truncate text-base font-semibold tracking-tight">
                {formatScheduleDayHeading(selectedYmd, scheduleTz, 'EEE, MMM d')}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Choose an available day.</p>
            </div>
            <CalendarIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          </header>

          <div className="w-full overflow-hidden">
            <Calendar
              mode="single"
              captionLayout="dropdown"
              selected={boundedSelectedDate}
              onSelect={(d) => {
                if (!d) return;
                setSelectedMinutes(null);
                setSelectedDate(d);
              }}
              disabled={{ before: today, after: horizonEnd }}
              startMonth={today}
              endMonth={horizonEnd}
            />
          </div>
        </section>

        <section className="rounded-xl border bg-card p-4">
          <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-medium text-muted-foreground">Time</p>
              <p className="mt-1 truncate text-base font-semibold tracking-tight sm:text-lg">
                {formatScheduleDayHeading(selectedYmd, scheduleTz, 'EEEE, MMMM d')}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">Select a time slot.</p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-10 w-10"
                onClick={() => {
                  const prev = addDays(boundedSelectedDate, -1);
                  setSelectedMinutes(null);
                  setSelectedDate(clampDate(prev, today, horizonEnd));
                }}
                disabled={boundedSelectedDate <= today}
                aria-label="Previous day"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-10 w-10"
                onClick={() => {
                  const next = addDays(boundedSelectedDate, 1);
                  setSelectedMinutes(null);
                  setSelectedDate(clampDate(next, today, horizonEnd));
                }}
                disabled={boundedSelectedDate >= horizonEnd}
                aria-label="Next day"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </header>

          <div
            className={cn(
              'mt-4 rounded-lg border bg-background p-2',
              isSlotsScrollable && 'max-h-[420px] overflow-auto'
            )}
          >
            {slots.length === 0 ? (
              <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                No slots available for this date.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {slots.map((s) => {
                  const selected = selectedMinutes === s.minutes;
                  return (
                    <Button
                      key={s.minutes}
                      type="button"
                      variant={selected ? 'default' : 'outline'}
                      className={cn(
                        'h-10 justify-center text-sm font-medium tabular-nums',
                        !selected && 'bg-background hover:bg-accent',
                        selected && 'shadow-sm ring-2 ring-primary/30 ring-offset-2 ring-offset-background'
                      )}
                      onClick={() => setSelectedMinutes(s.minutes)}
                    >
                      {s.label}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{tzLabel}</span>
            </div>
            {selectedMinutes !== null ? (
              <Badge variant="secondary">
                Selected: {formatScheduleDayHeading(selectedYmd, scheduleTz, 'EEE, MMM d')} •{' '}
                {formatWallMinutesOnDate(selectedYmd, selectedMinutes, scheduleTz)}
              </Badge>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

